import { useState, useEffect, useRef } from 'react';
import {
  Search,
  Loader2,
  ChevronDown,
  CheckCircle2,
  MessageSquare,
  CheckCheck,
  Clock,
  XCircle,
  Send,
  ArrowUpRight,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { parseDisplayName, formatDate } from '@/lib/utils';
import { useWhatsappHistory, useWhatsappStats } from '@/api';
import type { WhatsappHistory as WhatsappHistoryType } from '@/types/outreach';
import styles from './WhatsappHistory.module.css';

const STATUS_CONFIG: Record<
  string,
  { icon: typeof MessageSquare; className: string; label: string }
> = {
  QUEUED: { icon: Clock, className: 'statusQueued', label: 'Queued' },
  SENT: { icon: Send, className: 'statusSent', label: 'Sent' },
  DELIVERED: { icon: CheckCircle2, className: 'statusDelivered', label: 'Delivered' },
  READ: { icon: CheckCheck, className: 'statusRead', label: 'Read' },
  FAILED: { icon: XCircle, className: 'statusFailed', label: 'Failed' },
};

const StatCard = ({
  label,
  value,
  rate,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  rate?: number;
  icon: typeof MessageSquare;
  color: string;
}) => (
  <div className={styles.statCard}>
    <div className={styles.statCardHeader}>
      <span className={styles.statCardLabel}>{label}</span>
      <Icon className={`${styles.statCardIcon} ${styles[color]}`} />
    </div>
    <div className={styles.statCardValue}>{value.toLocaleString()}</div>
    {rate !== undefined && (
      <div className={styles.statCardRate}>
        {rate > 0 ? (
          <ArrowUpRight className={styles.statCardRateIcon} />
        ) : (
          <span className={styles.statCardRateDash}>—</span>
        )}
        <span>{rate}%</span>
      </div>
    )}
  </div>
);

const DetailRow = ({ label, value }: { label: string; value: string | null | undefined }) => {
  if (!value) return null;
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value}</span>
    </div>
  );
};

export const WhatsappHistory = () => {
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState('all');
  const [offset, setOffset] = useState(0);
  const [allItems, setAllItems] = useState<WhatsappHistoryType[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const prevKeyRef = useRef('');
  const queryClient = useQueryClient();

  const statsPeriod =
    period === 'all' ? 'all' : period === 'today' ? 'today' : period === 'week' ? '7d' : '30d';
  const { data, isLoading, isFetching } = useWhatsappHistory(search, period, offset);
  const {
    data: stats,
    isLoading: statsLoading,
    isFetching: statsFetching,
  } = useWhatsappStats(statsPeriod);

  const refreshing = isFetching || statsFetching;

  const handleRefresh = () => {
    setOffset(0);
    queryClient.invalidateQueries({ queryKey: ['whatsappHistory'] });
    queryClient.invalidateQueries({ queryKey: ['whatsappStats'] });
  };

  useEffect(() => {
    const key = `${search}|${period}`;
    if (key !== prevKeyRef.current) {
      prevKeyRef.current = key;
      setOffset(0);
      setAllItems([]);
    }
  }, [search, period]);

  useEffect(() => {
    if (data?.items) {
      if (offset === 0) {
        setAllItems(data.items);
      } else {
        setAllItems((prev) => [...prev, ...data.items]);
      }
    }
  }, [data, offset]);

  const total = data?.total ?? 0;
  const hasMore = allItems.length < total;

  const periodOptions: [string, string][] = [
    ['all', 'All time'],
    ['today', 'Today'],
    ['week', '7 days'],
    ['month', '30 days'],
  ];

  const getStatusBadge = (item: WhatsappHistoryType) => {
    const statusKey = item.status || 'SENT';
    const config = STATUS_CONFIG[statusKey] || STATUS_CONFIG.SENT;
    const Icon = config.icon;
    return (
      <span className={`${styles.statusBadge} ${styles[config.className]}`}>
        <Icon className={styles.statusIcon} />
        {config.label}
      </span>
    );
  };

  const formatTimestamp = (ts: string | null | undefined) => {
    if (!ts) return null;
    return new Date(ts).toLocaleString();
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h1 className={styles.title}>WhatsApp History</h1>
            <p className={styles.subtitle}>WhatsApp campaign analytics and message history.</p>
          </div>
          <div className={styles.headerActions}>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`${styles.refreshButton} ${refreshing ? styles.refreshButtonLoading : ''}`}
            >
              <RefreshCw className={`${styles.refreshIcon} ${refreshing ? styles.spinning : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Stats overview */}
        <div className={styles.statsGrid}>
          {statsLoading ? (
            [...Array(5)].map((_, i) => (
              <div key={i} className={styles.statCard}>
                <div className={styles.statCardHeader}>
                  <div className={styles.skeletonLine} style={{ width: '60%' }} />
                </div>
                <div className={styles.skeletonLine} style={{ width: '40%', height: '1.5rem' }} />
                <div
                  className={styles.skeletonLine}
                  style={{ width: '30%', height: '0.625rem', marginTop: '0.25rem' }}
                />
              </div>
            ))
          ) : stats ? (
            <>
              <StatCard
                label="Total Sent"
                value={stats.total_sent}
                icon={MessageSquare}
                color="colorEmerald"
              />
              <StatCard
                label="Delivered"
                value={stats.delivered}
                rate={stats.delivery_rate}
                icon={CheckCircle2}
                color="colorBlue"
              />
              <StatCard
                label="Read"
                value={stats.read}
                rate={stats.read_rate}
                icon={CheckCheck}
                color="colorViolet"
              />
              <StatCard label="Failed" value={stats.failed} icon={XCircle} color="colorRed" />
              <StatCard label="Pending" value={stats.pending} icon={Clock} color="colorZinc" />
            </>
          ) : null}
        </div>

        {/* Filters */}
        <div className={styles.filtersCard}>
          <div className={styles.filterRow}>
            <div className={styles.searchWrapper}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search by phone, name, or business..."
                className={styles.searchInput}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className={styles.periodBtns}>
              {periodOptions.map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setPeriod(value)}
                  className={`${styles.periodBtn} ${period === value ? styles.periodBtnActive : ''}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.filterMeta}>
            <span className={styles.filterCount}>
              {total.toLocaleString()} message{total !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableCard}>
          {isLoading && allItems.length === 0 ? (
            <div className={styles.loadingState}>
              <Loader2 className={styles.spinner} />
              <span className={styles.loadingText}>Loading history...</span>
            </div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Recipient</th>
                    <th className={styles.hiddenSm}>Business</th>
                    <th className={styles.hiddenMd}>Message</th>
                    <th className={styles.hiddenSm}>Sent</th>
                    <th style={{ textAlign: 'right' }}>Status</th>
                    <th style={{ width: '2rem' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {allItems.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={styles.emptyCell}>
                        {search || period !== 'all'
                          ? 'No messages match your filters.'
                          : 'No messages sent yet.'}
                      </td>
                    </tr>
                  ) : (
                    allItems.map((item) => {
                      const isExpanded = expandedId === item.id;
                      return (
                        <tr
                          key={item.id}
                          className={isExpanded ? styles.rowExpanded : ''}
                          onClick={() => setExpandedId(isExpanded ? null : item.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <div className={styles.recipientName}>
                              {item.person?.fullName || item.phoneNumberRecipient}
                            </div>
                            <div className={styles.recipientPhone}>{item.phoneNumberRecipient}</div>
                          </td>
                          <td className={styles.hiddenSm}>
                            {parseDisplayName(item.person?.place?.displayName)?.text || '—'}
                          </td>
                          <td className={`${styles.messageCell} ${styles.hiddenMd}`}>
                            {item.message}
                          </td>
                          <td className={`${styles.sentDate} ${styles.hiddenSm}`}>
                            {item.sentAt ? formatDate(item.sentAt) : '—'}
                          </td>
                          <td className={styles.statusCell}>{getStatusBadge(item)}</td>
                          <td>
                            <ChevronRight
                              className={`${styles.expandIcon} ${isExpanded ? styles.expandIconOpen : ''}`}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              {/* Expanded detail panel (rendered outside table for layout) */}
              {expandedId &&
                (() => {
                  const item = allItems.find((i) => i.id === expandedId);
                  if (!item) return null;
                  return (
                    <div className={styles.detailPanel}>
                      <div className={styles.detailGrid}>
                        <div className={styles.detailSection}>
                          <h4 className={styles.detailSectionTitle}>Delivery</h4>
                          <DetailRow label="Sent at" value={formatTimestamp(item.sentAt)} />
                          <DetailRow
                            label="Delivered at"
                            value={formatTimestamp(item.deliveredAt)}
                          />
                          <DetailRow label="Read at" value={formatTimestamp(item.readAt)} />
                          <DetailRow label="Status" value={item.status} />
                        </div>
                        {(item.errorMessage || item.failureReason) && (
                          <div className={styles.detailSection}>
                            <h4 className={styles.detailSectionTitle}>Error</h4>
                            <DetailRow label="Error message" value={item.errorMessage} />
                            <DetailRow label="Failure reason" value={item.failureReason} />
                          </div>
                        )}
                        <div className={styles.detailSection}>
                          <h4 className={styles.detailSectionTitle}>Content</h4>
                          <DetailRow label="Message" value={item.message} />
                          <DetailRow label="Template" value={item.template?.name || '—'} />
                        </div>
                      </div>
                    </div>
                  );
                })()}

              {hasMore && (
                <div className={styles.loadMoreWrapper}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOffset(allItems.length);
                    }}
                    disabled={isLoading}
                    className={styles.loadMoreBtn}
                  >
                    {isLoading ? (
                      <Loader2 className={`${styles.loadMoreIcon} ${styles.spinner}`} />
                    ) : (
                      <ChevronDown className={styles.loadMoreIcon} />
                    )}
                    Load more ({total - allItems.length} remaining)
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
